import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAuctionState } from './AuctionStateContext';
import { useContribution } from './ContributionContext';
import { affiliateService } from '../services/affiliateService';
import { tokenService } from '../services/tokenService';

// Create the context
export const AffiliateContext = createContext();

export const useAffiliate = () => {
    const context = useContext(AffiliateContext);
    if (!context) {
        throw new Error('useAffiliate must be used within an AffiliateProvider');
    }
    return context;
};

export const AffiliateProvider = ({ children }) => {
    const wallet = useWallet();
    const { currentDay } = useAuctionState();
    const { userContributions } = useContribution();
    
    // Affiliate state
    const [affiliateStats, setAffiliateStats] = useState({
        totalReferrals: 0,
        activeReferrals: 0,
        totalRewards: 0,
        pendingRewards: 0,
        claimedRewards: 0,
        referralLink: '',
        referralCode: '',
        referrer: null,
        isAffiliate: false
    });
    
    const [referralTasks, setReferralTasks] = useState({
        twitter: { completed: false, reward: 0 },
        discord: { completed: false, reward: 0 },
        telegram: { completed: false, reward: 0 }
    });
    
    const [isLoading, setIsLoading] = useState(false);
    
    // Initialize affiliate stats when wallet changes
    useEffect(() => {
        if (wallet.publicKey) {
            loadAffiliateStats();
        } else {
            // Reset state when wallet disconnects
            setAffiliateStats({
                totalReferrals: 0,
                activeReferrals: 0,
                totalRewards: 0,
                pendingRewards: 0,
                claimedRewards: 0,
                referralLink: '',
                referralCode: '',
                referrer: null,
                isAffiliate: false
            });
        }
    }, [wallet.publicKey]);
    
    // Load affiliate stats from service
    const loadAffiliateStats = useCallback(async () => {
        if (!wallet.publicKey) return;
        
        setIsLoading(true);
        try {
            // In production, this would call the smart contract
            // For now, we'll use local storage to simulate persistence
            const walletAddress = wallet.publicKey.toString();
            
            // Generate referral code if not exists
            let referralCode = localStorage.getItem(`referral_code_${walletAddress}`);
            if (!referralCode) {
                referralCode = generateReferralCode(walletAddress);
                localStorage.setItem(`referral_code_${walletAddress}`, referralCode);
            }
            
            // Generate referral link
            const referralLink = generateReferralLink(referralCode);
            
            // Get referrer if exists
            const referrer = localStorage.getItem(`referrer_${walletAddress}`);
            
            // Get affiliate stats from storage
            const storedStats = localStorage.getItem(`affiliate_stats_${walletAddress}`);
            let stats = {
                totalReferrals: 0,
                activeReferrals: 0,
                totalRewards: 0,
                pendingRewards: 0,
                claimedRewards: 0,
                referralLink,
                referralCode,
                referrer,
                isAffiliate: true
            };
            
            if (storedStats) {
                const parsedStats = JSON.parse(storedStats);
                stats = { ...stats, ...parsedStats };
            }
            
            setAffiliateStats(stats);
            
            // Load referral tasks
            const storedTasks = localStorage.getItem(`referral_tasks_${walletAddress}`);
            if (storedTasks) {
                setReferralTasks(JSON.parse(storedTasks));
            }
        } catch (error) {
            console.error("Error loading affiliate stats:", error);
        } finally {
            setIsLoading(false);
        }
    }, [wallet.publicKey]);
    
    // Generate referral code
    const generateReferralCode = useCallback((walletAddress) => {
        // Generate a short code based on wallet address
        // In production, this would be a more secure and unique code
        return walletAddress.substring(0, 8);
    }, []);
    
    // Generate referral link
    const generateReferralLink = useCallback((referralCode) => {
        // Generate a referral link with the code
        // In production, this would use the actual domain
        return `https://palmheist.io/?ref=${referralCode}`;
    }, []);
    
    // Set referrer
    const setReferrer = useCallback(async (referrerCode) => {
        if (!wallet.publicKey || !referrerCode) return false;
        
        try {
            const walletAddress = wallet.publicKey.toString();
            
            // Check if user already has a referrer
            const existingReferrer = localStorage.getItem(`referrer_${walletAddress}`);
            if (existingReferrer) {
                return false; // Already has a referrer
            }
            
            // Check if trying to refer self
            const ownReferralCode = localStorage.getItem(`referral_code_${walletAddress}`);
            if (ownReferralCode === referrerCode) {
                return false; // Cannot refer self
            }
            
            // In production, this would verify the referrer code on-chain
            // For now, we'll just save it to local storage
            localStorage.setItem(`referrer_${walletAddress}`, referrerCode);
            
            // Update affiliate stats
            setAffiliateStats(prev => ({
                ...prev,
                referrer: referrerCode
            }));
            
            // Update referrer's stats (in production, this would be done on-chain)
            // For now, we'll simulate it
            const referrerWallet = Object.keys(localStorage).find(key => {
                if (key.startsWith('referral_code_')) {
                    return localStorage.getItem(key) === referrerCode;
                }
                return false;
            });
            
            if (referrerWallet) {
                const referrerAddress = referrerWallet.replace('referral_code_', '');
                const storedStats = localStorage.getItem(`affiliate_stats_${referrerAddress}`);
                let stats = {
                    totalReferrals: 1,
                    activeReferrals: 1,
                    totalRewards: 0,
                    pendingRewards: 0,
                    claimedRewards: 0
                };
                
                if (storedStats) {
                    const parsedStats = JSON.parse(storedStats);
                    stats = {
                        ...parsedStats,
                        totalReferrals: (parsedStats.totalReferrals || 0) + 1,
                        activeReferrals: (parsedStats.activeReferrals || 0) + 1
                    };
                }
                
                localStorage.setItem(`affiliate_stats_${referrerAddress}`, JSON.stringify(stats));
            }
            
            return true;
        } catch (error) {
            console.error("Error setting referrer:", error);
            return false;
        }
    }, [wallet.publicKey]);
    
    // Complete a referral task
    const completeReferralTask = useCallback(async (platform) => {
        if (!wallet.publicKey || !platform) return false;
        
        try {
            const walletAddress = wallet.publicKey.toString();
            
            // Check if task already completed
            if (referralTasks[platform]?.completed) {
                return false;
            }
            
            // In production, this would verify the task completion on-chain
            // For now, we'll just update local storage
            
            // Calculate reward
            const reward = calculateTaskReward(platform);
            
            // Update tasks
            const updatedTasks = {
                ...referralTasks,
                [platform]: {
                    completed: true,
                    reward
                }
            };
            
            setReferralTasks(updatedTasks);
            localStorage.setItem(`referral_tasks_${walletAddress}`, JSON.stringify(updatedTasks));
            
            // Update affiliate stats
            const updatedStats = {
                ...affiliateStats,
                totalRewards: affiliateStats.totalRewards + reward,
                pendingRewards: affiliateStats.pendingRewards + reward
            };
            
            setAffiliateStats(updatedStats);
            localStorage.setItem(`affiliate_stats_${walletAddress}`, JSON.stringify(updatedStats));
            
            return true;
        } catch (error) {
            console.error(`Error completing ${platform} task:`, error);
            return false;
        }
    }, [wallet.publicKey, referralTasks, affiliateStats]);
    
    // Calculate task reward
    const calculateTaskReward = useCallback((platform) => {
        // In production, this would be based on the actual tokenomics
        // For now, we'll use fixed rewards
        const rewards = {
            twitter: 100,
            discord: 75,
            telegram: 50
        };
        
        return rewards[platform] || 0;
    }, []);
    
    // Calculate affiliate rewards
    const calculateAffiliateRewards = useCallback(() => {
        if (!wallet.publicKey) return 0;
        
        // In production, this would calculate rewards based on referrals' contributions
        // For now, we'll use a simplified model
        
        // 10M JINU for affiliate pool as per tokenomics
        const AFFILIATE_POOL = 10000000;
        
        // Calculate base rewards from referrals
        const baseRewards = affiliateStats.activeReferrals * 1000; // 1000 JINU per active referral
        
        // Calculate rewards from referral contributions (simulated)
        let contributionRewards = 0;
        
        // In production, this would track actual referral contributions
        // For now, we'll simulate it based on the number of active referrals
        if (affiliateStats.activeReferrals > 0) {
            // Assume each referral contributes an average of 1 SOL
            const estimatedReferralContributions = affiliateStats.activeReferrals * 1;
            
            // Reward is 5% of referral contributions in JINU
            contributionRewards = estimatedReferralContributions * 0.05 * 10000; // Assuming 1 SOL = 10,000 JINU
        }
        
        // Calculate task rewards
        const taskRewards = Object.values(referralTasks).reduce((sum, task) => {
            return sum + (task.completed ? task.reward : 0);
        }, 0);
        
        return {
            baseRewards,
            contributionRewards,
            taskRewards,
            totalRewards: baseRewards + contributionRewards + taskRewards
        };
    }, [wallet.publicKey, affiliateStats, referralTasks]);
    
    // Claim rewards
    const claimRewards = useCallback(async () => {
        if (!wallet.publicKey || affiliateStats.pendingRewards <= 0) return false;
        
        setIsLoading(true);
        try {
            // In production, this would call the smart contract to claim rewards
            // For now, we'll simulate it
            
            // Update affiliate stats
            const updatedStats = {
                ...affiliateStats,
                pendingRewards: 0,
                claimedRewards: affiliateStats.claimedRewards + affiliateStats.pendingRewards
            };
            
            setAffiliateStats(updatedStats);
            
            const walletAddress = wallet.publicKey.toString();
            localStorage.setItem(`affiliate_stats_${walletAddress}`, JSON.stringify(updatedStats));
            
            return true;
        } catch (error) {
            console.error("Error claiming rewards:", error);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [wallet.publicKey, affiliateStats]);
    
    // Check if user is a referral
    const isReferral = useCallback(() => {
        return !!affiliateStats.referrer;
    }, [affiliateStats.referrer]);
    
    // Get referrer stats
    const getReferrerStats = useCallback(async () => {
        if (!wallet.publicKey || !affiliateStats.referrer) return null;
        
        try {
            // In production, this would get the referrer's stats from the blockchain
            // For now, we'll simulate it
            
            // Find referrer wallet address
            const referrerWallet = Object.keys(localStorage).find(key => {
                if (key.startsWith('referral_code_')) {
                    return localStorage.getItem(key) === affiliateStats.referrer;
                }
                return false;
            });
            
            if (!referrerWallet) return null;
            
            const referrerAddress = referrerWallet.replace('referral_code_', '');
            const storedStats = localStorage.getItem(`affiliate_stats_${referrerAddress}`);
            
            if (!storedStats) return null;
            
            return JSON.parse(storedStats);
        } catch (error) {
            console.error("Error getting referrer stats:", error);
            return null;
        }
    }, [wallet.publicKey, affiliateStats.referrer]);

    return (
        <AffiliateContext.Provider value={{
            // State
            affiliateStats,
            referralTasks,
            isLoading,
            
            // Methods
            loadAffiliateStats,
            generateReferralLink,
            setReferrer,
            completeReferralTask,
            calculateAffiliateRewards,
            claimRewards,
            isReferral,
            getReferrerStats
        }}>
            {children}
        </AffiliateContext.Provider>
    );
};
